import {
  LLMBrain,
  MemoryStore,
  MockProvider,
  PROTOCOL_VERSION,
  RoomCore,
  quickIntent,
  type ClientMessage,
  type DeviceInfo,
  type GameState,
  type Outbound,
  type ServerMessage,
} from "@squire/core";
import { storage } from "../system/storage.js";
import type { ConnectionStatus, Transport } from "./transport.js";

const KEY = "squire-local-state";

/** MemoryStore that survives reloads via localStorage (state only, history capped). */
class PersistentStore extends MemoryStore {
  override async load(): Promise<GameState | null> {
    const raw = storage.get(KEY);
    if (raw) {
      try {
        this.state = JSON.parse(raw) as GameState;
      } catch {
        this.state = null;
      }
    }
    return super.load();
  }
  override async save(prev: GameState | null, next: GameState): Promise<void> {
    await super.save(prev, next);
    storage.set(KEY, JSON.stringify(next));
  }
}

/**
 * The whole room running in the browser (demo, offline, soak tests). Same RoomCore as the server,
 * rule-based brain instead of an LLM, reminders via one timer.
 */
export class LocalTransport implements Transport {
  onMessage: (msg: ServerMessage) => void = () => {};
  onStatus: (status: ConnectionStatus) => void = () => {};
  readonly room: RoomCore;
  private alarm: ReturnType<typeof setTimeout> | null = null;
  private chain: Promise<void> = Promise.resolve();

  constructor(
    private readonly device: DeviceInfo,
    private readonly tz: string,
    private readonly clock: () => number = () => Date.now(),
  ) {
    this.room = new RoomCore(
      new PersistentStore(),
      new LLMBrain(new MockProvider()),
      { now: () => this.clock() },
      quickIntent,
    );
  }

  start(): void {
    this.onStatus("online");
    this.send({ t: "hello", v: PROTOCOL_VERSION, device: this.device, tz: this.tz, visible: true });
  }

  stop(): void {
    if (this.alarm) clearTimeout(this.alarm);
    this.alarm = null;
  }

  send(msg: ClientMessage): boolean {
    if (msg.t === "ping") return true;
    // Serialize like a Durable Object input gate.
    this.chain = this.chain
      .then(async () => this.deliver(await this.room.dispatch(this.device, msg)))
      .catch((e) => console.error("local room", e));
    return true;
  }

  private deliver(out: Outbound[]) {
    for (const o of out)
      if (o.to === "all" || o.to === this.device.id) queueMicrotask(() => this.onMessage(o.msg));
    this.scheduleAlarm();
  }

  private scheduleAlarm() {
    if (this.alarm) clearTimeout(this.alarm);
    this.alarm = null;
    const at = this.room.nextAlarmAt();
    if (at === null) return;
    // Long timers drift on sleeping devices; re-check at least every 5 minutes.
    const delay = Math.min(5 * 60_000, Math.max(1000, at - this.clock()));
    this.alarm = setTimeout(() => {
      this.chain = this.chain.then(async () => this.deliver(await this.room.alarm()));
    }, delay);
  }
}
